import { useFlags as useLDFlags } from 'launchdarkly-react-client-sdk'
import React, { createContext, useContext, useMemo, useState } from 'react'
import { useUser } from '@paintscout/ui-business'
import type { ReactNode } from 'react'

/**
 * Manual-only flag context
 */
type Flags = Record<string, any>
interface ManualFlagsContextValue {
  flags: Flags
  setFlag: (key: string, value: any) => void
}
const ManualFlagsContext = createContext<ManualFlagsContextValue | undefined>(undefined)

/**
 * Manual provider
 */
interface ManualFlagsProviderProps {
  initialFlags?: Flags
  children: ReactNode
}
export const ManualFlagsProvider = ({ initialFlags = {}, children }: ManualFlagsProviderProps) => {
  const [flags, setFlags] = useState<Flags>(initialFlags)
  const setFlag = (key: string, value: any) => setFlags((prev) => ({ ...prev, [key]: value }))

  return <ManualFlagsContext.Provider value={{ flags, setFlag }}>{children}</ManualFlagsContext.Provider>
}

/**
 * Wrapper hook
 * - If inside ManualFlagsProvider, use manual flags
 * - Otherwise, fallback to LDProvider flags
 */
export const useFlags = (): Flags => {
  const manualContext = useContext(ManualFlagsContext)
  const { nonBilling, isTrial, isCrew, trialExpired, isPainter } = useUser()
  const ldFlags = useLDFlags()

  // Memoized so callers get a stable reference when nothing has actually changed — without this,
  // every render produced a brand-new object, which broke any effect/callback keyed on `flags`
  // (e.g. useBetaFeatures's re-seed effect looped forever, freezing the tab; see ENG-1560). Still
  // computed even when manualContext is set, since hooks can't be called conditionally — the
  // manual path just never returns it. This memo is only as stable as `ldFlags` itself; a consumer
  // that can't tolerate the LD SDK ever churning its own reference should add its own bail-out too
  // (useBetaFeatures does, as a second layer of defense).
  const derivedFlags = useMemo(() => {
    const qualifiesForCRM = (nonBilling && ldFlags.nonBillingCrmGrant) || (isTrial && !trialExpired)
    const shouldAlwaysShowCRM = qualifiesForCRM && !isCrew && ldFlags.crmVisible

    return {
      ...ldFlags,
      crm: shouldAlwaysShowCRM || ldFlags.crm,
      posthogSessionReplay: isPainter || ldFlags.posthogSessionReplay
    }
  }, [ldFlags, nonBilling, isTrial, isCrew, trialExpired, isPainter])

  if (manualContext) return manualContext.flags

  return derivedFlags
}
